import { useCallback, useEffect, useRef } from 'react';
import { Recorder } from '@/lib/audio/recorder';

type UseAudioRecorderOptions = {
  onAudioRecorded: (base64Audio: string) => void;
  requestStream: () => Promise<MediaStream | null>;
  onChunkAnalyzed?: (analysis: { rmsDbfs: number; peakDbfs: number; isSilent: boolean }) => void;
  sampleRate?: number;
};

const BUFFER_SIZE = 4800;

export default function useAudioRecorder({
  onAudioRecorded,
  requestStream,
  onChunkAnalyzed,
  sampleRate = 24000
}: UseAudioRecorderOptions) {
  const recorderRef = useRef<Recorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isActiveRef = useRef(false);
  const bufferRef = useRef<Uint8Array>(new Uint8Array());
  const wasPreviousChunkSilentRef = useRef(false);

  const isSilentPcm16Chunk = (chunk: Uint8Array): boolean => {
    // A fully closed gain gate produces exact zeros. Dropping these avoids
    // sending continuous muted audio that can increase end-to-end latency.
    for (let i = 0; i < chunk.length; i += 1) {
      if (chunk[i] !== 0) {
        return false;
      }
    }
    return true;
  };

  const toBase64 = (data: Uint8Array): string => {
    let binary = '';
    for (let i = 0; i < data.length; i++) {
      binary += String.fromCharCode(data[i]);
    }
    return btoa(binary);
  };

  const appendToBuffer = (chunk: Uint8Array) => {
    const existing = bufferRef.current;
    const merged = new Uint8Array(existing.length + chunk.length);
    merged.set(existing);
    merged.set(chunk, existing.length);
    bufferRef.current = merged;
  };

  const flushBuffer = useCallback(() => {
    const buffer = bufferRef.current;
    if (buffer.length === 0) {
      return;
    }
    const toSend = buffer;
    bufferRef.current = new Uint8Array();
    onAudioRecorded(toBase64(toSend));
  }, [onAudioRecorded]);

  const handleAudioData = useCallback(
    (arrayBuffer: ArrayBuffer) => {
      if (!isActiveRef.current) {
        return;
      }

      const uint8Array = new Uint8Array(arrayBuffer);
      const int16Array = new Int16Array(
        uint8Array.buffer,
        uint8Array.byteOffset,
        Math.floor(uint8Array.byteLength / 2)
      );
      const isSilent = isSilentPcm16Chunk(uint8Array);
      let peak = 0;
      let sumSquares = 0;
      for (let i = 0; i < int16Array.length; i += 1) {
        const normalized = int16Array[i] / 32768;
        const abs = Math.abs(normalized);
        if (abs > peak) {
          peak = abs;
        }
        sumSquares += normalized * normalized;
      }
      const rms = int16Array.length > 0 ? Math.sqrt(sumSquares / int16Array.length) : 0;
      const peakDbfs = 20 * Math.log10(Math.max(peak, 1e-8));
      const rmsDbfs = 20 * Math.log10(Math.max(rms, 1e-8));
      onChunkAnalyzed?.({ rmsDbfs, peakDbfs, isSilent });

      if (isSilent) {
        // Flush buffered speech once when we transition into silence so
        // trailing speech doesn't wait indefinitely for BUFFER_SIZE.
        if (!wasPreviousChunkSilentRef.current && bufferRef.current.length > 0) {
          flushBuffer();
        }
        wasPreviousChunkSilentRef.current = true;
        return;
      }

      wasPreviousChunkSilentRef.current = false;
      appendToBuffer(uint8Array);

      while (bufferRef.current.length >= BUFFER_SIZE) {
        const chunk = bufferRef.current.slice(0, BUFFER_SIZE);
        bufferRef.current = bufferRef.current.slice(BUFFER_SIZE);
        onAudioRecorded(toBase64(chunk));
      }
    },
    [onAudioRecorded, onChunkAnalyzed, flushBuffer]
  );

  const ensureRecorderStarted = useCallback(async () => {
    if (!recorderRef.current) {
      recorderRef.current = new Recorder({
        sampleRate,
        onAudioData: handleAudioData
      });
    }

    if (!streamRef.current) {
      streamRef.current = await requestStream();
    }

    if (!streamRef.current) {
      throw new Error('Unable to access microphone stream');
    }

    if (!recorderRef.current.isReady()) {
      await recorderRef.current.start(streamRef.current);
    }
  }, [handleAudioData, requestStream, sampleRate]);

  const start = useCallback(async () => {
    await ensureRecorderStarted();
    isActiveRef.current = true;
    wasPreviousChunkSilentRef.current = false;
  }, [ensureRecorderStarted]);

  const stop = useCallback(() => {
    isActiveRef.current = false;
    flushBuffer();
    bufferRef.current = new Uint8Array();
    wasPreviousChunkSilentRef.current = false;
  }, [flushBuffer]);

  const warmup = useCallback(async () => {
    await ensureRecorderStarted();
  }, [ensureRecorderStarted]);

  useEffect(() => {
    return () => {
      recorderRef.current?.dispose();
      bufferRef.current = new Uint8Array();
      streamRef.current = null;
      isActiveRef.current = false;
    };
  }, []);

  return { start, stop, warmup };
}

